import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import ModalSection from '../ModalSection';
import PromptModel from '../../../../models/PromptModel';
import RightModalLayout from '../../../../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../../../../constants/actionsConstants';
import QuillComponent, { getPlainTextLength } from '../QuillComponent';

const ROLE_AND_GOAL_MAX_LENGTH = 350;
const KEY_INFO_MAX_LENGTH = 750;
const CLOSE_DELAY_MS = 1000;

const KEY_INFO_FIELDS = [
  {
    type: 'payment_methods',
    titleKey: 'config.role_and_goal.payment_methods_title',
    descriptionKey: 'config.role_and_goal.payment_methods_description'
  },
  {
    type: 'locations',
    titleKey: 'config.role_and_goal.locations_title',
    descriptionKey: 'config.role_and_goal.locations_description'
  }
];

const emptyKeyInfo = () => ({
  payment_methods: { id: null, value: '', loadFailed: false },
  locations: { id: null, value: '', loadFailed: false }
});

const isNotFound = (error) => error?.status === 404;

const readPrompt = async (agentId, type) => {
  try {
    const response = await PromptModel.get(agentId, { type });
    const prompt = (response?.data || []).find((item) => item.type === type);

    return {
      id: prompt?.id || null,
      value: prompt?.prompt || '',
      failed: false
    };
  } catch (error) {
    if (isNotFound(error)) {
      return { id: null, value: '', failed: false };
    }

    return { id: null, value: '', failed: true };
  }
};

const persistPrompt = (agentId, {
  id, type, value, allowBlank
}) => {
  const blank = getPlainTextLength(value) === 0;

  if (blank && !allowBlank) {
    if (!id) return Promise.resolve({ type, action: 'skipped' });

    return PromptModel.delete(agentId, id).then(() => ({ type, action: 'deleted' }));
  }

  if (id) {
    return PromptModel.update(agentId, id, { prompt: value }).then(() => ({
      type,
      action: 'updated',
      id
    }));
  }

  return PromptModel.save(agentId, { type, prompt: value }).then((result) => ({
    type,
    action: 'created',
    id: result?.id || null
  }));
};

const PromptEditor = ({
  name, value, onChange, maxLength, loading, loadFailed, loadingLabel, errorLabel
}) => {
  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-[180px]">
        <div className="tw-text-gray-500">{loadingLabel}</div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <p className="tw-text-xs tw-text-red-500 tw-py-4" role="alert">
        {errorLabel}
      </p>
    );
  }

  return (
    <QuillComponent
      name={name}
      value={value || ''}
      onChange={onChange}
      maxLength={maxLength}
      overflowHidden={false}
      style={{ height: '180px' }}
    />
  );
};

const GoalModal = ({ agentId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('ChatbotIA');
  const closeTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const tRef = useRef(t);
  tRef.current = t;

  const [roleAndGoal, setRoleAndGoal] = useState('');
  const [promptId, setPromptId] = useState(null);
  const [roleLoadFailed, setRoleLoadFailed] = useState(false);
  const [keyInfo, setKeyInfo] = useState(emptyKeyInfo);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const isOverLimit = getPlainTextLength(roleAndGoal) > ROLE_AND_GOAL_MAX_LENGTH
    || KEY_INFO_FIELDS.some((field) => (
      getPlainTextLength(keyInfo[field.type].value) > KEY_INFO_MAX_LENGTH
    ));

  useEffect(() => () => {
    mountedRef.current = false;
    clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => {
    if (!agentId) {
      showtoast(tRef.current('config.error_loading'));
      setLoading(false);
      return undefined;
    }

    let active = true;

    const loadGoalContent = async () => {
      setLoading(true);

      const [roleResult, ...keyResults] = await Promise.all([
        readPrompt(agentId, 'role_and_goal'),
        ...KEY_INFO_FIELDS.map((field) => readPrompt(agentId, field.type))
      ]);

      if (!active) return;

      setRoleAndGoal(roleResult.value);
      setPromptId(roleResult.id);
      setRoleLoadFailed(roleResult.failed);
      setKeyInfo(Object.fromEntries(KEY_INFO_FIELDS.map((field, index) => [
        field.type,
        {
          id: keyResults[index].id,
          value: keyResults[index].value,
          loadFailed: keyResults[index].failed
        }
      ])));
      setLoading(false);

      if (roleResult.failed) {
        showtoast(tRef.current('config.role_and_goal.load_blocked'));
      } else if (keyResults.some((result) => result.failed)) {
        showtoast(tRef.current('config.error_loading'));
      }
    };

    loadGoalContent();

    return () => {
      active = false;
    };
  }, [agentId]);

  const toggleGoalModal = () => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const rememberSavedIds = (results) => {
    const fulfilled = results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value);

    const createdRole = fulfilled.find((item) => (
      item?.type === 'role_and_goal' && item.action === 'created' && item.id
    ));

    if (createdRole) setPromptId(createdRole.id);

    setKeyInfo((current) => {
      const next = { ...current };

      fulfilled.forEach((item) => {
        if (!item || !next[item.type]) return;

        if (item.action === 'created' && item.id) {
          next[item.type] = { ...next[item.type], id: item.id };
        }

        if (item.action === 'deleted') {
          next[item.type] = { ...next[item.type], id: null };
        }
      });

      return next;
    });
  };

  const onSubmit = async () => {
    if (!agentId || roleLoadFailed) {
      showtoast(roleLoadFailed
        ? t('config.role_and_goal.load_blocked')
        : t('config.error_loading'));
      return;
    }

    const tasks = [
      persistPrompt(agentId, {
        id: promptId,
        type: 'role_and_goal',
        value: roleAndGoal,
        allowBlank: true
      })
    ];

    KEY_INFO_FIELDS.forEach((field) => {
      const current = keyInfo[field.type];
      if (current.loadFailed) return;

      tasks.push(persistPrompt(agentId, {
        id: current.id,
        type: field.type,
        value: current.value,
        allowBlank: false
      }));
    });

    setSaving(true);

    const results = await Promise.allSettled(tasks);
    if (!mountedRef.current) return;

    rememberSavedIds(results);

    const rejected = results.find((result) => result.status === 'rejected');
    if (rejected) {
      setSaving(false);
      showtoast(rejected.reason?.message || t('config.save_error'));
      return;
    }

    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;

      showtoast(t('config.save_success'));
      setSaving(false);
      toggleGoalModal();
    }, CLOSE_DELAY_MS);
  };

  const updateKeyInfo = (type, value) => {
    setKeyInfo((current) => ({
      ...current,
      [type]: { ...current[type], value }
    }));
  };

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={t('config.role_and_goal.modal_title')}
        submitAction={onSubmit}
        submitLabel={t('guides.sidebar_create.update')}
        submitDisabled={saving || loading || isOverLimit || roleLoadFailed}
        submitTooltip={roleLoadFailed ? t('config.role_and_goal.load_blocked') : undefined}
      >
        <ModalSection
          title={t('config.role_and_goal.section_1_title')}
          description={t('config.role_and_goal.section_1_description')}
          afterContent={(
            <PromptEditor
              name="role_and_goal"
              value={roleAndGoal}
              onChange={setRoleAndGoal}
              maxLength={ROLE_AND_GOAL_MAX_LENGTH}
              loading={loading}
              loadFailed={roleLoadFailed}
              loadingLabel={t('config.loading')}
              errorLabel={t('config.role_and_goal.load_blocked')}
            />
          )}
        />
        {KEY_INFO_FIELDS.map((field) => (
          <ModalSection
            key={field.type}
            title={t(field.titleKey)}
            description={t(field.descriptionKey)}
            afterContent={(
              <PromptEditor
                name={field.type}
                value={keyInfo[field.type].value}
                onChange={(value) => updateKeyInfo(field.type, value)}
                maxLength={KEY_INFO_MAX_LENGTH}
                loading={loading}
                loadFailed={keyInfo[field.type].loadFailed}
                loadingLabel={t('config.loading')}
                errorLabel={t('config.error_loading')}
              />
            )}
          />
        ))}
        <div
          className="tw-bg-blue-50 tw-border tw-border-blue-100 tw-border-solid tw-flex tw-gap-1 tw-items-center tw-p-4 tw-relative tw-rounded-lg"
          role="note"
        >
          <i
            aria-hidden="true"
            className="mi-info-outline tw-shrink-0 tw-leading-none tw-text-base tw-text-[#198FCC]"
          />
          <p className="tw-flex-1 tw-font-medium tw-leading-4 tw-min-h-px tw-min-w-px tw-not-italic tw-text-blue-500 tw-text-xs tw-whitespace-pre-wrap">
            {t('config.role_and_goal.clarity_alert')}
          </p>
        </div>
      </RightModalLayout>
    </div>
  );
};

export default GoalModal;
